/* eslint semi: 1 */
import { computed, inject, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { tagsStore } from '@/pinia/stores/tagsStore';
import { popIdsStore } from '@/pinia/stores/popIdsStore';
import { COMPONENT_KEY } from '@/pinia/stores/storeUtils';

import { useMediaUpdate } from '@/composables/media/use-media-update';
import { useMediaSelection } from '@/composables/media/use-media-selection';
import { useMediaListStore } from '@/pinia/stores/media/mediaListStore';
import { useMediaQueryStore } from '@/pinia/stores/media/mediaQueryStore';
import _ from 'lodash';
import EventBus from '@/components/mixins/event-bus-mixin';

/**
 * Composable for managing the state and logic related to the media context menu on the media files page.
 * Handles:
 * - Building the tags summary list for autocomplete suggestions, based on the tagsStore state and LED tag filtering.
 * - Building the pop ID summary list from the popIdsStore.
 * - Responding to media data updates emitted by the MediaMenu component, applying changes to the media list and triggering necessary refreshes.
 * - Controlling the visibility of the context menu based on media selection.
 *
 * @param {Object} params
 * @param {Ref} params.tagSliderElRef - Ref to the tag slider element, used to trigger refreshes after mutations
 * @param {Ref} params.popIdSliderElRef - Ref to the pop ID slider element, used to trigger refreshes after mutations
 */
export function useMediaContextMenu ({ tagSliderElRef, popIdSliderElRef }) {
  const $utils = inject('$utils');
  const $currentUser = inject('$currentUser');
  const $router = inject('$routerInstance');
  const $apiError = inject('$apiError');
  const { t } = useI18n();
  const mediaListStore = useMediaListStore();
  const mediaQueryStore = useMediaQueryStore();
  const mediaUpdate = useMediaUpdate();
  const mediaSelection = useMediaSelection();
  const tagStore = tagsStore();
  const popIdStore = popIdsStore(COMPONENT_KEY.FILES_SCREEN)();
  const ledPopIdStore = popIdsStore(COMPONENT_KEY.LED_SCREEN)();
  const videowallPopIdStore = popIdsStore(COMPONENT_KEY.VIDEOWALL_SCREEN)();

  const confirmForceToDeleteMedia = ref({ showDialog: false, items: [], confirmed: false });
  const hideMediaMenuComponentTemplate = ref(false);

  const isLedResolutionVisible = ref(false);
  const isVideowallResolutionVisible = ref(false);

  // Context menu visibility is driven by whether any item is selected.
  const isContextMenuVisible = computed(() => mediaSelection.hasSelection.value);

  const resourceTags = computed(() => tagStore.resourceTags);
  const ledTags = computed(() => tagStore.ledTags);
  const playerTags = computed(() => tagStore.playerTags);

  /**
   * Reactive list of tag options for the media files page, formatted for UI.
   * - Combines resource tags and player tags.
   * - Excludes resource tags that are inlined with any LED tag (LED-specific tags
   *   are managed on the LED page and should not appear as general suggestions) for
   *   non-LED/Videowall pages.
   * - For the LED/Videowall pages, applies additional filtering to only include
   *   LED tags that are inline with the current user's tags and permissions.
   * - Deduplicates by name.
   * - Formatted via $utils.filterTagOptionsForUI for use in tag input components.
   */
  const tagsSummary = computed(() => {
    let resourceTagsList = [...(resourceTags.value ?? [])];
    const playerTagsList = [...(playerTags.value ?? [])];
    let led = [...(ledTags.value ?? [])];

    // Note: Translated logic from the old Media page.
    // Only resource tags are filtered by LED inline rules — player tags are always included.
    if (mediaQueryStore.isLed) {
      // For LED page, If current user has CREATE rights +
      // user tags OR CREATE_LOCK (modify) rights + user
      // tags, filter led tags inline with assigned user tags
      if (
        ($currentUser?.hasGroupUserCreate() || $currentUser?.hasGroupUserCreateLock()) &&
        !_.isEmpty($currentUser?.getUserTags())
      ) {
        led = led.filter(ledTag => !_.isEmpty(_.intersection(ledTag.inline, $currentUser.getUserTags())));
      }
      // Get only resource tags that are inline with LED
      resourceTagsList = resourceTagsList.filter(resourceTag => !_.isEmpty(led.find(tag => tag.inline.includes(resourceTag.name))));
    } else if (mediaQueryStore.isVideowall) {
      // Get only resource tags that are inline with LED (videowall uses ledTags for inline filtering)
      resourceTagsList = resourceTagsList.filter(resourceTag => !_.isEmpty(led.find(tag => tag.inline.includes(resourceTag.name))));
    } else {
      // Get only resource tags NOT inline with LED
      resourceTagsList = resourceTagsList.filter(resourceTag => _.isEmpty(led.find(tag => tag.inline.includes(resourceTag.name))));
    }

    return $utils.filterTagOptionsForUI({ tags: _.uniqBy([...resourceTagsList, ...playerTagsList], 'name') });
  });

  /**
   * Reactive list of pop ID options for the media files page, formatted for UI.
   * - For the LED and Videowall pages, only includes pop IDs relevant to the page type.
   * - For the general media page, includes all pop IDs.
   * - Deduplicates by name.
   * Note: The PopIdSlider component is triggering the fetching of pop IDs
   * for different page types on mount (LED/Videowall/Files page), so all
   * pop ID data should be available in the pop id pinia store.
   */
  const popIdsSummary = computed(() => popIdStore.popIds ?? []);
  const ledPopIdsSummary = computed(() => ledPopIdStore.popIds ?? []);
  const videowallPopIdsSummary = computed(() => videowallPopIdStore.popIds ?? []);
  const popIdsSummaryByPage = computed(() => {
    if (mediaQueryStore.isLed) {
      return ledPopIdsSummary.value;
    } else if (mediaQueryStore.isVideowall) {
      return videowallPopIdsSummary.value;
    }

    return popIdsSummary.value;
  });

  async function handleErrorNotification (error) {
    if (error?.response?.status) {
      $apiError.handleAPIRequestError(error, {
        showDialogNotification: true
      });
    } else {
      EventBus.$emit('SHOW_CUSTOM_NOTIFICATION', {
        type: 'error',
        alert: {
          message: t('playerSetup.UnknownError')
        },
        callbacks: {
          eventOnClose: () => {}
        }
      });
    }
  }

  /**
   * Applies successful PUT responses to the list store.
   * @param {Array} updatedMediaList - Items returned by the MediaMenu put action
   */
  function applyPutResults (updatedMediaList) {
    for (const updatedMedia of updatedMediaList) {
      if (updatedMedia.response.type === 'success') {
        mediaUpdate.syncUpdated(updatedMedia.response.responseData);
      }
    }
    hideMediaMenuComponentTemplate.value = false;
  }

  /**
 * Applies successful DELETE responses to the list store.
 * Items that return 409/412 (still in use) are collected and surfaced via the
 * force-delete confirmation dialog instead of being removed immediately.
 * @param {Array} updatedMediaList - Items returned by the MediaMenu delete action
 */
  function applyDeleteResults (updatedMediaList) {
    const forceDeleteItems = [];

    for (const mediaItem of updatedMediaList) {
      const responseStatus = mediaItem.response?.responseData?.response?.status;
      if (responseStatus && [409, 412].includes(responseStatus)) {
        // Media is in use in projects — collect for force-delete confirmation
        forceDeleteItems.push(mediaItem);
        continue;
      }

      if (mediaItem.response.type === 'success') {
        const resourceId = mediaItem.origDetails?.resourceId ?? mediaItem.resourceId;
        mediaListStore.removeItem(resourceId);
      }
    }

    if (forceDeleteItems.length > 0) {
      confirmForceToDeleteMedia.value = { showDialog: true, items: forceDeleteItems, confirmed: false };
      hideMediaMenuComponentTemplate.value = true;
    }
  }

  /**
   * Shared cleanup that runs after any put or delete mutation.
   * Clears the selection (unless it was an auto-save) and triggers a tag-slider refresh.
   * @param {Object} data - The payload.data object from the MediaMenu change event
   */
  function applyPostMutationCleanup (data) {
    if (!data.autoSave) {
      mediaSelection.clearSelection();
    }

    // Notify the tag slider to refresh so tag counts stay accurate
    tagSliderElRef?.value?.refreshTagList?.();
    popIdSliderElRef?.value?.refreshPopIdList?.();
  }

  /**
   * Handles the UPDATE_MEDIA_DATA action from the MediaMenu component.
   * @param {Object} data - event.payload.data from the 'media-menu-change' emit
   */
  function handleUpdateMediaData (data) {
    // Always reset the force-delete confirmation on any new update event
    confirmForceToDeleteMedia.value = { showDialog: false, items: [], confirmed: false };

    // if any item has failed updates/deletes, show the
    // error first but continue with successful updates.
    const failedUpdates = (data.updatedMedia ?? []).filter((i) =>
      i.response.type === 'error' &&
      ![409, 412].includes(i.response?.responseData?.response?.status)
    );
    if (failedUpdates.length > 0 && failedUpdates[0].response.responseData) {
      handleErrorNotification(failedUpdates[0].response.responseData);
    }

    if (data.requestType === 'put') applyPutResults(data.updatedMedia ?? []);
    if (data.requestType === 'delete') applyDeleteResults(data.updatedMedia ?? []);

    // Only run the post-mutation cleanup if there were no failed request type.
    if (!failedUpdates.length && ['put', 'delete'].includes(data.requestType)) {
      applyPostMutationCleanup(data);
    }
  }

  /**
   * Handles changes emitted from the MediaMenu component.
   * Delegates to focused handlers based on the action type.
   * @param {Object} event - Event emitted by media-menu via 'media-menu-change'
   */
  function handleMediaMenuChange (event) {
    if (event.action === 'UPDATE_MEDIA_DATA') {
      handleUpdateMediaData(event.payload.data);
    }

    if (event.action === 'RESET_MEDIA_MENU_TEMPLATE_VISIBILITY') {
      hideMediaMenuComponentTemplate.value = false;
      mediaSelection.clearSelection();
    }
  }

  function showLedResolutionMenu () {
    isLedResolutionVisible.value = true;
    isVideowallResolutionVisible.value = false;
    mediaSelection.clearSelection();
  }

  function showVideowallResolutionMenu () {
    isLedResolutionVisible.value = false;
    isVideowallResolutionVisible.value = true;
    mediaSelection.clearSelection();
  }

  function hideResolutionMenus () {
    isLedResolutionVisible.value = false;
    isVideowallResolutionVisible.value = false;
  }

  function handleLedResolutionMenuChange (event) {
    if (event.action !== 'NEW_SETTING_CREATED') return;

    // redirect to create project
    const dimension = `${event.payload.data.width}x${event.payload.data.height}`;
    let path = '/template/default/led';
    const query = {
      dimensions: dimension
    };

    if (event.payload.data.type === 'videowall') {
      query.videowallTags = $utils.parseLedTag(event.payload.data.tag).queryStringValue;
      path = '/template/default/videowall';
    } else if (event.payload.data.type === 'led') {
      query.ledTags = $utils.parseLedTag(event.payload.data.tag).queryStringValue;
    }

    $router.push({
      path,
      query
    });
  }

  return {
    isContextMenuVisible,
    tagsSummary,
    popIdsSummary,
    ledPopIdsSummary,
    videowallPopIdsSummary,
    popIdsSummaryByPage,

    isLedResolutionVisible,
    isVideowallResolutionVisible,

    handleMediaMenuChange,
    handleUpdateMediaData,
    showLedResolutionMenu,
    showVideowallResolutionMenu,
    hideResolutionMenus,
    handleLedResolutionMenuChange
  };
}
